import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, ChevronDown } from 'lucide-react';
import { useProducts } from '../../../context/ProductContext';
import { categoriesMap } from '../../../utils/categories';

export const CategoryDropdown: React.FC = () => {
  const { selectedCategory, setSelectedCategory } = useProducts();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setIsOpen(false);
    setExpandedCategory(null);
  };

  const handleMainCategoryClick = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
        <Package className="text-orange-500" size={16} />
        Categoría
      </label>
      
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 border-2 border-orange-100 rounded-lg hover:border-orange-200 transition-colors"
      >
        <span className="text-gray-700 truncate">
          {selectedCategory || 'Todas las categorías'}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown size={20} className="text-orange-500" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-white rounded-lg shadow-xl border border-orange-100 max-h-[70vh] overflow-y-auto"
          >
            <div className="p-2 space-y-1">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleCategorySelect('')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                  selectedCategory === ''
                    ? 'bg-orange-500 text-white'
                    : 'hover:bg-orange-50'
                }`}
              >
                Todas las categorías
              </motion.button>

              {Object.entries(categoriesMap).map(([category, subcategories]) => (
                <motion.div key={category} layout>
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    onClick={() => handleMainCategoryClick(category)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center justify-between ${
                      expandedCategory === category ? 'bg-orange-50' : 'hover:bg-orange-50'
                    }`}
                  >
                    <span className="font-medium">{category}</span>
                    <motion.div
                      animate={{ rotate: expandedCategory === category ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown size={16} className="text-orange-500" />
                    </motion.div>
                  </motion.button>

                  <AnimatePresence>
                    {expandedCategory === category && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="ml-4 space-y-1"
                      >
                        {subcategories.map((subcategory) => (
                          <motion.button
                            key={subcategory}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleCategorySelect(subcategory)}
                            className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                              selectedCategory === subcategory
                                ? 'bg-orange-500 text-white'
                                : 'hover:bg-orange-50'
                            }`}
                          >
                            {subcategory}
                          </motion.button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};